import { Box, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { getSectionInput, saveSectionInput } from "../../../api/qbrInputs";
import { patchQbrRepInput } from "../../../redux/slice/kpi-widget-metrics";
import { showSnackbar } from "../../../redux/slice/snackbar";
import { BODY, MUTED } from "./tokens";
import { SaveStatus, YourInputBadge } from "./primitives";

/** Wait this long after the last keystroke before autosaving. */
const AUTOSAVE_DEBOUNCE_MS = 1500;

const FREE_TEXT_MAX = 2000;

/** Show the character counter only once the value is this close to the cap. */
const COUNTER_VISIBLE_WITHIN = 200;

// A labelled free-text field that persists itself through qbrInputsService, keyed per-quarter by the parent so each quarter gets a fresh instance seeded from that quarter's initialValue (prevents cross-quarter bleed since a single reused instance can't reset reliably when two quarters share the same string).
// Save behavior: seeded once at mount from initialValue (legacy callers fall back to getSectionInput); debounced autosave 1.5s after the last keystroke with immediate save on blur; dirty-check skips unchanged saves; a pending debounce is FLUSHED (not dropped) on unmount so in-flight edits land under the quarter they were typed in; a confirmed save is mirrored into the bulk qbr_report Redux slice via patchQbrRepInput; value is capped at FREE_TEXT_MAX to match the backend; on failure status flips to "error" with a snackbar and lastSavedRef is not advanced so the next edit retries; quarterId is forwarded to saveSectionInput so edits made on a prior quarter land under that quarter.
export default function SectionInputField({
  sectionId,
  fieldKey,
  label,
  placeholder,
  initialValue,
  quarterId,
}) {
  const dispatch = useDispatch();
  const hasInitialValue = typeof initialValue === "string";
  const [value, setValue] = useState(hasInitialValue ? initialValue : "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const lastSavedRef = useRef(hasInitialValue ? initialValue : "");
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  // Value for which a PATCH is currently in flight (null when idle) — lets a blur after an already-firing autosave skip a duplicate request, and lets a slower earlier response bow out instead of regressing lastSavedRef.
  const savingValueRef = useRef(null);
  // Latest edited value, read by the unmount flush since the effect cleanup can't close over the live `value` state.
  const latestValueRef = useRef(hasInitialValue ? initialValue : "");

  // Dirty-checked save; saveSectionInput resolves {ok, error?} without throwing. Stable per instance since its inputs are fixed for the life of a quarter-keyed instance, so the mount effect can depend on it without re-running every render.
  const save = useCallback(
    async (next) => {
      // Skip if already persisted or an identical save is already in flight, so a debounced autosave immediately followed by a blur (or a flush on switch) can't fire a duplicate PATCH.
      if (next === lastSavedRef.current) return;
      if (next === savingValueRef.current) return;
      savingValueRef.current = next;
      if (mountedRef.current) setStatus("saving");
      // quarterId is fixed for this instance, so the write always lands under the quarter the text was typed in, including a flush fired as the field unmounts on a switch.
      const result = await saveSectionInput(sectionId, fieldKey, next, {
        quarterId,
      });
      // Only the latest attempt settles the field, so an older/slower response can't regress lastSavedRef, re-mirror stale text into Redux, or flip the badge back to "Saved".
      const isLatestAttempt = savingValueRef.current === next;
      if (isLatestAttempt) savingValueRef.current = null;
      if (result && result.ok) {
        if (isLatestAttempt) {
          lastSavedRef.current = next;
          // Mirror the confirmed write into the bulk slice (even when unmounted, since this is store state) so revisiting this quarter re-seeds from the saved value; guarded to API-backed fields so a legacy localStorage caller can't inject an unrelated key.
          if (hasInitialValue) {
            dispatch(
              patchQbrRepInput({
                quarterId: quarterId ?? null,
                fieldName: fieldKey,
                fieldValue: next,
              }),
            );
          }
          if (mountedRef.current) setStatus("saved");
        }
        return;
      }
      // Failure: keep lastSavedRef at the previously-persisted value so a follow-up edit retries; a failed flush on an unmounted field correctly leaves Redux unchanged.
      if (!mountedRef.current || !isLatestAttempt) return;
      setStatus("error");
      dispatch(
        showSnackbar({
          message: "Failed to save your note. Please try again.",
          severity: "error",
        }),
      );
    },
    [sectionId, fieldKey, quarterId, hasInitialValue, dispatch],
  );

  // Mount/unmount only (deps are stable per instance); legacy non-API callers load their value here (API-backed fields are seeded by the useState initializer above); on unmount — which, via the quarterId key, is exactly a quarter switch — any pending debounced save is FLUSHED rather than dropped.
  useEffect(() => {
    mountedRef.current = true;
    let active = true;
    if (!hasInitialValue) {
      getSectionInput(sectionId, fieldKey).then((saved) => {
        if (!active) return;
        lastSavedRef.current = saved;
        latestValueRef.current = saved;
        setValue(saved);
      });
    }
    return () => {
      active = false;
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        save(latestValueRef.current);
      }
    };
  }, [sectionId, fieldKey, hasInitialValue, save]);

  // Adopts the server value when it arrives/changes while the field is clean, since the bulk QBR GET resolves after this field mounts; comparing through refs keeps this off the keystroke path, and the dirty guard means an in-progress edit is never overwritten by a late-arriving fetch.
  useEffect(() => {
    if (!hasInitialValue) return;
    if (latestValueRef.current !== lastSavedRef.current) return;
    if (latestValueRef.current === initialValue) return;
    lastSavedRef.current = initialValue;
    latestValueRef.current = initialValue;
    setValue(initialValue);
  }, [initialValue, hasInitialValue]);

  const handleChange = (event) => {
    // Cap client-side so a paste (which some browsers let through native maxLength) can never exceed the backend limit; the controlled value is the single source of truth, so truncating here covers type, paste, and drop.
    const next = event.target.value.slice(0, FREE_TEXT_MAX);
    setValue(next);
    latestValueRef.current = next;
    // Clear any success/error badge the moment the rep starts typing again so the indicator doesn't lie about the field's live state.
    if (status === "saved" || status === "error") setStatus("idle");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      save(next);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    save(value);
  };

  const showCounter = value.length >= FREE_TEXT_MAX - COUNTER_VISIBLE_WITHIN;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          minHeight: 20,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: BODY }}>
          {label}
        </Typography>
        <YourInputBadge />
        <Box sx={{ flex: 1 }} />
        <SaveStatus status={status} />
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={3}
        // Cap the field at 3 rows: past that the textarea stops growing and scrolls instead of pushing the layout down.
        maxRows={3}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        variant="outlined"
        inputProps={{ maxLength: FREE_TEXT_MAX }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            borderRadius: "8px",
            p: 1.5,
            alignItems: "flex-start",
            "& fieldset": { borderColor: "#e0e0e0" },
          },
          "& textarea": {
            fontSize: 13,
            lineHeight: "20px",
            color: BODY,
            // Height is fixed at 3 rows (minRows === maxRows), so disable the manual drag handle that would otherwise fight the auto-sized textarea.
            resize: "none",
          },
          "& textarea::placeholder": {
            fontSize: 13,
            color: MUTED,
            opacity: 1,
          },
        }}
      />
      {showCounter && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 11,
            fontWeight: 600,
            color: value.length >= FREE_TEXT_MAX ? "#D32F2F" : MUTED,
            textAlign: "right",
          }}
        >
          {value.length}/{FREE_TEXT_MAX}
        </Typography>
      )}
    </Box>
  );
}
